import { post } from 'api/api';
import { API_ROUTES } from 'api/apiRoutes';
import { SSO_TEST_CALLBACK_EVENT_TYPE, SSO_TEST_CHANNEL } from 'consts';
import { useToast } from 'hooks';
import { useEffect } from 'react';
import { IDPResponse } from 'types/api';
import { ToastSeverity } from 'types/toast';
import { isBroadcastChannelSupported, isInIframe } from 'utils/isInIframe';

interface SSOTestCallbackMessage {
	type: 'sso-test-callback';
	code?: string;
	error?: string;
}

interface Options {
	onError: (error: string) => void;
	onSuccess: (result: IDPResponse) => void;
	onSettled: () => void;
}

const useSSOTestCallbackListener = ({
	onError,
	onSuccess,
	onSettled
}: Options) => {
	const { addToast } = useToast();

	useEffect(() => {
		if (!isInIframe() || !isBroadcastChannelSupported()) return () => null;

		let channel: BroadcastChannel;
		try {
			channel = new BroadcastChannel(SSO_TEST_CHANNEL);
		} catch {
			/* istanbul ignore next */
			return () => null;
		}

		channel.onmessage = async (event: MessageEvent<SSOTestCallbackMessage>) => {
			const msg = event.data;
			if (msg?.type !== SSO_TEST_CALLBACK_EVENT_TYPE) return;

			if (msg.error) {
				onError(msg.error);
				addToast({
					severity: ToastSeverity.Error,
					content: intl.tr('ssoConfig.testPage.error', { error: msg.error })
				});
				onSettled();
				return;
			}

			if (msg.code) {
				try {
					const res = await post<{ token: string }, IDPResponse>(
						API_ROUTES.ssoTest.end,
						{ token: msg.code }
					);
					onSuccess(res.data);
				} catch {
					/* istanbul ignore next  */
					addToast({
						severity: ToastSeverity.Error,
						content: intl.tr('ssoConfig.testPage.failed')
					});
				} finally {
					onSettled();
				}
			}
		};

		return () => channel.close();
	}, [addToast, onError, onSuccess, onSettled]);
};

export default useSSOTestCallbackListener;
